import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { BlocToaster } from "./components/toast";
import { isPlatformPath } from "./lib/host";
import { startOsThemeWatch } from "./lib/theme";
// Side-effect import: registers the typed router union for both chunks.
import "./routers/register";
import "./styles/global.css";

// 再 deploy 後に古いタブが遅延 chunk（ハッシュ付き）を要求すると、asset は消えていて
// SPA フォールバックの index.html（text/html）が返り、module 読み込みが落ちる。
// 1 回だけ自動リロードして新バンドルに乗り換える（連続失敗時は無限リロードを避けて放置）。
window.addEventListener("vite:preloadError", (event) => {
  const key = "bloc:preload-reloaded";
  if (sessionStorage.getItem(key) === location.href) return;
  sessionStorage.setItem(key, location.href);
  event.preventDefault();
  location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, err) => {
        // Don't retry 401/403 — let the route guard redirect to login.
        if (
          err instanceof Error &&
          "status" in err &&
          (err as { status: number }).status &&
          (err as { status: number }).status < 500
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

async function bootstrap() {
  // `system` 選択中に OS の配色が変わったら追従する（初期適用は index.html の
  // インライン script が済ませている）。
  startOsThemeWatch();

  // Decide which router (and chunk) to load based on the current URL pathname.
  // Path-based admin URL: PlatformAdmin / shared (`/`, `/p/...`, `/login`)
  // gets the platform router; Org-scoped (`/o/{slug}/...`) gets the org
  // router. The other chunk is never fetched.
  const { createAppRouter } = isPlatformPath(window.location.pathname)
    ? await import("./routers/platform")
    : await import("./routers/org");

  const router = createAppRouter(queryClient);

  const rootEl = document.getElementById("root");
  if (!rootEl) throw new Error("#root not found");

  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {/* トーストは両チャンク共通でここに 1 回だけマウント（components/toast.tsx）。 */}
        <BlocToaster />
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
